import {
    AccessibilityWindowContainer,
    SliderBodyContainer,
    StyledContentContainer,
    StyledLabel,
    StyledSubmenuItem,
    StyledText,
    StyledTitle,
    StyledTitleContainer,
    StyledToggleButton, StyledToggleContainer,
    SubmenuContainer,
    SubmenuItemContainer
} from "./styled";
import {Switch} from "../../switch";
import {NavigationAndActionClose} from "../../icons";
import {SliderBody} from "../../slider/SliderBody";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {SufixType} from "../../input/common/SufixType";
import {useAppDispatch, useAppSelector} from "../../../hooks/appHooks";
import { AccessibilitySelectors } from '../redux';
import {
    setFontSizeLevel,
    setLineSpacingLevel,
    setWordSpacingLevel,
    toggleAmblyopia,
    toggleDyslexia,
    toggleUnderline
} from "../redux/slice";

export interface AccessibilityWindowProps {
    onClose: () => void;
}

export const AccessibilityWindow: React.FC<AccessibilityWindowProps> = ({onClose}) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const isDyslexiaOn = useAppSelector(AccessibilitySelectors.selectIsDyslexiaOn);
    const isAmblyopiaOn = useAppSelector(AccessibilitySelectors.selectIsAmblyopiaOn);
    const isUnderlineOn = useAppSelector(AccessibilitySelectors.selectIsUnderlineOn);
    const fontSizeLevel = useAppSelector(AccessibilitySelectors.selectFontSizeLevel);
    const lineSpacingLevel = useAppSelector(AccessibilitySelectors.selectLineSpacingLevel);
    const wordSpacingLevel = useAppSelector(AccessibilitySelectors.selectWordSpacingLevel);

    const [selectedStyling, setSelectedStyling] = useState<string>('fontSize');

    const handleCurrentSlider = () => {
        switch (selectedStyling){
            case 'fontSize': return fontSizeLevel;
            case 'lineSpacing': return lineSpacingLevel;
            case 'wordSpacing': return wordSpacingLevel;
            default: return fontSizeLevel
        }
    }

    useEffect(() => {
        if(isDyslexiaOn){
            document.body.style.setProperty("--main-font", "OpenDyslexic, sans-serif");
        }else {
            document.body.style.setProperty("--main-font", "'UniCredit', 'Helvetica Neue', sans-serif");
        }
    }, [isDyslexiaOn]);

    useEffect(() => {
        if(isAmblyopiaOn){
            document.body.style.setProperty("--contrast-background", "black");
        }else {
            document.body.style.setProperty("--contrast-background", "transparent");
        }
    }, [isAmblyopiaOn]);

    useEffect(() => {
        if(isUnderlineOn){
            document.body.style.setProperty("--text-underline", "underline");
        }else {
            document.body.style.setProperty("--text-underline", "none");
        }
    }, [isUnderlineOn]);

    useEffect(() => {
       document.body.style.setProperty("--main-font-size", 16 + ((fontSizeLevel - 1) * 2) + "px");
        document.body.style.setProperty("--h5-font-size", 14 + ((fontSizeLevel - 1) * 2) + "px");
        document.body.style.setProperty("--h4-font-size", 18 + ((fontSizeLevel - 1) * 2) + "px");
        document.body.style.setProperty("--h3-font-size", 20 + ((fontSizeLevel - 1) * 2) + "px");
        document.body.style.setProperty("--h2-font-size", 42 + ((fontSizeLevel - 1) * 2) + "px");
        document.body.style.setProperty("--h1-font-size", 50 + ((fontSizeLevel - 1) * 2) + "px");
    }, [fontSizeLevel]);


    useEffect(() => {
        document.body.style.setProperty("--main-line-height", 24 + ((lineSpacingLevel - 1) * 2) + "px");
        document.body.style.setProperty("--h1-line-height", 50 + ((lineSpacingLevel - 1) * 2) + "px");
        document.body.style.setProperty("--h2-line-height", 42 + ((lineSpacingLevel - 1) * 2) + "px");
        document.body.style.setProperty("--h3-line-height", 22 + ((lineSpacingLevel - 1) * 2) + "px");
        document.body.style.setProperty("--h4-line-height", 24 + ((lineSpacingLevel - 1) * 2) + "px");
        document.body.style.setProperty("--h5-line-height", 15 + ((lineSpacingLevel - 1) * 2) + "px");
        document.body.style.setProperty("--h6-line-height", 12 + ((lineSpacingLevel - 1) * 2) + "px");
    }, [lineSpacingLevel]);

    useEffect(() => {
        document.body.style.setProperty("--word-spacing", ((wordSpacingLevel - 1) * 2) + "px");
    }, [wordSpacingLevel]);


    const handleSliderChange = (value: string) => {
        if (value) {
            switch (selectedStyling){
                case 'fontSize': return dispatch(setFontSizeLevel(parseInt(value)));
                case 'lineSpacing': return dispatch(setLineSpacingLevel(parseInt(value)));
                case 'wordSpacing': return dispatch(setWordSpacingLevel(parseInt(value)));
                default: return null;
            }
        }
    }

    return (
        <>
            <AccessibilityWindowContainer>
                <StyledTitleContainer>
                    <StyledTitle>{t('accessibility.title')}</StyledTitle>
                    <StyledToggleButton onClick={onClose} icon={<NavigationAndActionClose />} />
                </StyledTitleContainer>
                <StyledLabel>{t('accessibility.subtitle')}</StyledLabel>

                <StyledToggleContainer>
                    <StyledContentContainer>
                        <StyledText>{t('accessibility.dyslexiaFont')}</StyledText>
                        <Switch onChange={() => {dispatch(toggleDyslexia())}} value={isDyslexiaOn}></Switch>
                    </StyledContentContainer>
                    <StyledContentContainer>
                        <StyledText>{t('accessibility.amblyopiaContrast')}</StyledText>
                        <Switch onChange={() => {dispatch(toggleAmblyopia())}} value={isAmblyopiaOn}></Switch>
                    </StyledContentContainer>
                    <StyledContentContainer>
                        <StyledText>{t('accessibility.clickableElements')}</StyledText>
                        <Switch onChange={() => {dispatch(toggleUnderline())}} value={isUnderlineOn}></Switch>
                    </StyledContentContainer>
                </StyledToggleContainer>
                <StyledContentContainer>
                    <StyledText>{t('accessibility.fontChange')}</StyledText>
                </StyledContentContainer>
                <SubmenuContainer>
                    <SubmenuItemContainer>
                        <StyledSubmenuItem
                            selected={selectedStyling === 'fontSize'}
                            onClick={() => {setSelectedStyling('fontSize')}}>
                            {t('accessibility.submenu.fontSize')}
                        </StyledSubmenuItem>
                        <StyledSubmenuItem
                            selected={selectedStyling === 'lineSpacing'}
                            onClick={() => {setSelectedStyling('lineSpacing')}}>
                            {t('accessibility.submenu.lineSpacing')}
                        </StyledSubmenuItem>
                        <StyledSubmenuItem
                            selected={selectedStyling === 'wordSpacing'}
                            onClick={() => {setSelectedStyling('wordSpacing')}}>
                            {t('accessibility.submenu.wordSpacing')}
                        </StyledSubmenuItem>
                    </SubmenuItemContainer>
                </SubmenuContainer>
                <SliderBodyContainer>
                    <SliderBody  min={1}
                                 max={8}
                                 notches={6}
                                 valueType={SufixType.None}
                                 currentSliderValue={handleCurrentSlider()}
                                 handleSliderChange={handleSliderChange}>
                    </SliderBody>
                </SliderBodyContainer>
            </AccessibilityWindowContainer>
        </>
    );
}